/* eslint-disable no-shadow */
import React, { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import { Grid, Button, TextField, Typography, useTheme, useMediaQuery } from "@mui/material";
import { Alert, Autocomplete } from "@mui/material";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "axios";
import _ from "lodash";

import updateUser from "../../../redux/users/thunks/updateUser";
import InputField from "../InputField";
import SuspenseFallback from "../../../components/shared/SuspenseFallback";
import { green } from "../../../constants/colors";
import SocialMediaInput from "../../../components/shared/SocialMediaInput";
import FacebookIcon from "../../../assets/icons/FacebookIcon";

const useStyles = makeStyles((theme) => ({
  main: {
    padding: "30px",
    backgroundColor: theme.palette.bg,
    [theme.breakpoints.down("md")]: {
      padding: "0",
    },
  },
  detailTitle: {
    fontSize: "14px",
    fontFamily: "SFProText-Regular",
  },
  submit: {
    width: "150px",
    marginTop: "3%",
    border: "1px solid #4CAF50",
    height: "36px",
    fontFamily: "SFProText-Semibold",
    fontSize: "20px",
    borderRadius: "8px",
    padding: "10px",
    backgroundColor: "#4CAF50",
    color: "#FFF",
    fontWeight: "550",
    transition: "all 0.4s ease",

    "&:hover": {
      backgroundColor: "#FFFFFF",
      border: "1px solid #4CAF50",
      color: "#4CAF50",
    },
  },
  swalHeader: {
    fontFamily: "SFProText-Semibold",
    fontSize: "14px",
  },
  description: {
    fontSize: "14px",
    fontFamily: "SFProText-Regular",
    padding: 0,
    margin: 0,
  },
  button: {
    padding: "6px 31px",
    fontFamily: "SFProText-Semibold",
    borderRadius: "16px",
    fontSize: "13px",
    "&:hover": {
      cursor: "pointer",
    },
  },
  swalCancelButton: {
    color: "white",
    backgroundColor: "#B7B7B7",
    border: "none",
    marginRight: "8px",
  },
  popup: {
    width: "260px !important",
    height: "173px !important",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    margin: 0,
    padding: "1%",
  },
  actions: {
    flexWrap: "nowrap !important",
  },
  content: {
    width: "100%",
    padding: 0,
    margin: 0,
  },
  autoCompleteRoot: {
    marginLeft: "22px",
    "& .MuiInputBase-root": {
      borderRadius: "16px",
    },
    [theme.breakpoints.down("md")]: {
      marginLeft: 0,
    },
  },
  swalConfirmButton: {
    backgroundColor: "inherit !important",
    border: `1px solid ${theme.palette.primary.light} !important`,
    color: `${green} !important`,
  },
}));

const EditProfile = ({ autoReplies }) => {
  const classes = useStyles();
  const [categories, setCategories] = useState([]);
  const {
    updateUserErrorMsg,
    errorMsg,
    userInfo: {
      payment_method_id,
      email,
      auto_reply_msg,
      subscription_welcome_msg,
      second_auto_reply_msg,
      auto_reminder_msg,
      auto_reminder_msg_every,
      auto_stop_msg,
      first_name,
      last_name,
      display_name,
      business_category,
      description,
      website,
      work_email,
      address,
      phone_number,
      avatar_url,
      _id,
      loading,
      token,
      twilio_number,
      facebook,
      twitter,
      youtube,
      instagram,
      tiktok,
      linkedin,
      other_social_type,
      subscription_link,
      auto_replies_status: {
        set_auto_reply_msg,
        set_second_auto_reply_msg,
        set_subscription_welcome_msg,
        set_auto_reminder_msg,
        set_auto_stop_msg,
      },
    },
  } = useSelector((state) => state.user);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const isLg = useMediaQuery(theme.breakpoints.down("lg"));

  const [addAutoReplyMsg, setAddAutoReplyMsg] = useState(false);
  const [addAutoStopMsg, setAddAutoStopMsg] = useState(false);
  const [addSecondAutoReplyMsg, setAddSecondAutoReplyMsg] = useState(false);
  const [addAutoReminderMsg, setAddAutoReminderMsg] = useState(false);
  const [addSubscriptionWelcomeMsg, setAddSubscriptionWelcomeMsg] = useState(false);
  const [isFormUpdated, setIsFormUpdated] = useState(true);
  // const [linkSave, setLinkSave] = useState();
  const twitterData = twitter
    ? {
        value: "twitter",
        label: "Twitter",
        url: twitter,
      }
    : {};
  const youtubeData = youtube
    ? {
        value: "youtube",
        label: "Youtube",
        url: youtube,
      }
    : {};
  const instgramData = instagram
    ? {
        value: "instagram",
        label: "Instgram",
        url: instagram,
      }
    : {};
  const tiktokData = tiktok
    ? {
        value: "tiktok",
        label: "Tiktok",
        url: tiktok,
      }
    : {};
  const linkedinData = linkedin
    ? {
        value: "linkedin",
        label: "Linkedin",
        url: linkedin,
      }
    : {};
  const otherSocailTypeData = other_social_type
    ? {
        value: "other_social_type",
        label: "Other",
        url: other_social_type,
      }
    : {};
  const allSocial = [
    twitterData,
    instgramData,
    youtubeData,
    linkedinData,
    tiktokData,
    otherSocailTypeData,
  ];
  const socialData = [
    {
      value: "facebook",
      label: "Facebook",
      icon: <FacebookIcon />,
      url: facebook,
    },
  ].concat(allSocial.filter((element) => Object.keys(element).length !== 0));

  const initialValues = {
    first_name: "" || first_name,
    display_name: "" || display_name,
    description: "" || description,
    business_category: "" || business_category,
    website: website ? (website.endsWith("/") ? website : `${website}/`) : "",
    work_email: "" || work_email,
    address: "" || address,
    last_name: "" || last_name,
    phone_number: "" || phone_number,
    email: "" || email,
    auto_reply_msg,
    second_auto_reply_msg,
    auto_stop_msg,
    subscription_welcome_msg,
    auto_reminder_msg,
    set_auto_reply_msg,
    set_second_auto_reply_msg,
    set_subscription_welcome_msg,
    set_auto_reminder_msg,
    set_auto_stop_msg,
    auto_reminder_msg_every,
    avatar_url: "",
    socialData,
    // socialData: [
    //   {
    //     value: "facebook",
    //     label: "Facebook",
    //     icon: <FacebookIcon />,
    //     url: facebook
    //   },
    // ]
  };

  const formik = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      // debugger;
      const socialData = {};
      // eslint-disable-next-line no-unused-expressions
      values?.socialData?.map((ele) => {
        socialData[ele.value] = ele.url;
      });
      const payload = values;
      delete payload.socialData;
      const dataToUpdate = {
        ...payload,
        ...socialData,
        // socialData: values?.socialData?.map(el => el.url),
        first_name: values.first_name,
        last_name: values.last_name,
        display_name: values.display_name,
        business_category: values.business_category,
        description: values.description,
        website: values.website.endsWith("/") ? values.website : `${values.website}/` || "",
        work_email: values.work_email,
        address: values.address,
        phone_number: values.phone_number,
        auto_reply_msg: values.auto_reply_msg || "",
        auto_stop_msg: values.auto_stop_msg || "",
        subscription_welcome_msg: values.subscription_welcome_msg || "",
        second_auto_reply_msg: values.second_auto_reply_msg || "",
        auto_reminder_msg: values.auto_reminder_msg || "",
        auto_reminder_msg_every: values.auto_reminder_msg_every || "",
        set_auto_stop_msg: values.set_auto_stop_msg || false,
        set_auto_reply_msg: values.set_auto_reply_msg || false,
        set_second_auto_reply_msg: values.set_second_auto_reply_msg || false,
        set_subscription_welcome_msg: values.set_subscription_welcome_msg || false,
        set_auto_reminder_msg: values.set_auto_reminder_msg || false,
        email: values.email,
        avatar_url: values.avatar_url || avatar_url,
        auto_replies_status: {
          set_auto_reply_msg: values.set_auto_reply_msg || false,
          set_second_auto_reply_msg: values.set_second_auto_reply_msg || false,
          set_subscription_welcome_msg: values.set_subscription_welcome_msg || false,
          set_auto_reminder_msg: values.set_auto_reminder_msg || false,
          set_auto_stop_msg: values.set_auto_stop_msg || false,
        },
      };
      dispatch(
        updateUser(_id, dataToUpdate, token, undefined, undefined, undefined, classes, autoReplies),
      );
    },
    initialValues,
    // validationSchema: validationSchema(addAutoReplyMsg, addSecondAutoReplyMsg, addAutoReminderMsg),
  });

  useEffect(() => {
    if (addAutoReplyMsg !== formik.values.set_auto_reply_msg)
      setAddAutoReplyMsg(formik.values.set_auto_reply_msg);
  }, [formik.values.set_auto_reply_msg]);

  useEffect(() => {
    if (addSecondAutoReplyMsg !== formik.values.set_second_auto_reply_msg)
      setAddSecondAutoReplyMsg(formik.values.set_second_auto_reply_msg);
  }, [formik.values.set_second_auto_reply_msg]);

  useEffect(() => {
    if (addAutoReminderMsg !== formik.values.set_auto_reminder_msg)
      setAddAutoReminderMsg(formik.values.set_auto_reminder_msg);
  }, [formik.values.set_auto_reminder_msg]);

  useEffect(() => {
    if (addSubscriptionWelcomeMsg !== formik.values.set_subscription_welcome_msg)
      setAddSubscriptionWelcomeMsg(formik.values.set_subscription_welcome_msg);
  }, [formik.values.set_subscription_welcome_msg]);

  useEffect(() => {
    if (addAutoStopMsg !== formik.values.set_auto_stop_msg)
      setAddAutoStopMsg(formik.values.set_auto_stop_msg);
  }, [formik.values.set_auto_stop_msg]);

  useEffect(() => {
    setIsFormUpdated((prevState) => {
      if (!_.isEqual(initialValues, formik.values)) {
        return false;
      } else {
        return true;
      }
    });
  }, [formik.values]);

  useEffect(() => {
    (async () => {
      const {
        data: { data: categories },
      } = await axios.get("/api/v1/business-categories", {
        headers: {
          Authorization: token,
        },
      });
      setCategories(categories.categories);
    })();
  }, []);

  return (
    <div className={classes.main}>
      <Grid container>
        {loading ? <SuspenseFallback /> : null}
        {errorMsg && updateUserErrorMsg ? (
          <Alert severity="error">{updateUserErrorMsg}</Alert>
        ) : null}
        {/* Edit Pofile */}
        {/* {!autoReplies ? ( */}
        <>
          <InputField
            imageInput
            avatarURL={avatar_url}
            setFieldValue={(filedName, file) => formik.setFieldValue(filedName, file)}
            first_name={first_name}
            last_name={last_name}
            id="avatar_url"
            name="avatar_url"
            onChange={formik.handleChange}
            value={formik.values.avatar_url}
            error={formik.errors.avatar_url}
          />
          <InputField
            id="first_name"
            name="first_name"
            label={t("First Name")}
            placeholder={t("First Name")}
            onChange={formik.handleChange}
            value={formik.values.first_name}
            error={formik.errors.first_name}
            maxLength={50}
          />
          <InputField
            id="last_name"
            name="last_name"
            label={t("Last Name")}
            placeholder={t("Last Name")}
            onChange={formik.handleChange}
            value={formik.values.last_name}
            error={formik.errors.last_name}
            maxLength={50}
          />
          <InputField
            id="email"
            name="email"
            label={t("Email")}
            placeholder={t("Email")}
            onChange={formik.handleChange}
            value={formik.values.email}
            error={formik.errors.email}
            maxLength={256}
          />
          <InputField
            id="phone_number"
            name="phone_number"
            label={t("Phone")}
            placeholder={t("Phone")}
            onChange={formik.handleChange}
            value={formik.values.phone_number}
            error={formik.errors.phone_number}
            maxLength={13}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography style={{ marginTop: 30 }} variant="h6">
              Contact information
            </Typography>
            <Typography variant="subtitle1">Add some contact details for your business</Typography>
          </div>
          <InputField
            id="display_name"
            name="display_name"
            label={t("Display Name")}
            placeholder={t("Display Name")}
            onChange={formik.handleChange}
            value={formik.values.display_name}
            error={formik.errors.display_name}
            maxLength={50}
          />
          <InputField
            disabled
            id="twilio_number"
            name="twilio_number"
            label={t("WhatsApp Number")}
            placeholder={t("WhatsApp Number")}
            value={
              !payment_method_id
                ? "***********"
                : twilio_number?.phone_number
                  ? twilio_number.phone_number
                  : twilio_number
            }
            maxLength={13}
          />

          <InputField
            disabled
            id="subscription_link"
            name="subscription_link"
            label={t("Subscription Link")}
            placeholder={t("Subscription Link")}
            value={
              !payment_method_id
                ? "***********"
                : !subscription_link
                  ? "______"
                  : `https://admin.rasilni.com/subscribe/${subscription_link}`
            }
          />
          {/* Business Category */}
          <Grid item container xs={12} alignItems="center" sx={{ justifyContent: "space-between" }}>
            {/* Grid1: label */}
            <Grid
              item={true}
              xs={2}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                marginTop: isMd && "14px",
              }}
            >
              <Typography className={classes.detailTitle}>Category</Typography>
            </Grid>
            <Grid item={true} xs={isMd ? 12 : isLg ? 8 : 10}>
              <Autocomplete
                onChange={(e, selectedCategory) => {
                  formik.setFieldValue("business_category", selectedCategory._id);
                }}
                id="business_category"
                name="business_category"
                defaultValue={
                  categories.find(
                    (item) => item._id.toString() === formik.values?.business_category?.toString(),
                  ) || null
                }
                value={
                  categories.find(
                    (item) => item._id.toString() === formik.values?.business_category?.toString(),
                  ) || null
                }
                onFocus={() => formik.setFieldError("business_category", "")}
                classes={{
                  inputRoot: classes.autoCompleteInputContainer,
                  root: classes.autoCompleteRoot,
                  input: formik.errors?.business_category
                    ? classes.autoCompleteInputError
                    : classes.autoCompleteInput,
                }}
                options={categories}
                getOptionLabel={(option) => option.title}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    fullWidth
                    placeholder={t("Your Business Category")}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
          </Grid>
          <InputField
            setAutoReply
            textarea
            id="description"
            name="description"
            label={t("Description about your account/company")}
            placeholder={t("Description about your account/company")}
            onChange={formik.handleChange}
            value={formik.values.description}
            error={formik.errors.description}
            maxLength={512}
          />
          <InputField
            id="address"
            name="address"
            label={t("Address")}
            placeholder={t("Address")}
            onChange={formik.handleChange}
            value={formik.values.address || ""}
            error={formik.errors.address}
            maxLength={256}
          />
          <InputField
            id="work_email"
            name="work_email"
            label={t("Work Email")}
            placeholder={t("Work Email")}
            onChange={formik.handleChange}
            value={formik.values.work_email || ""}
            error={formik.errors.work_email}
            maxLength={128}
          />
          <InputField
            id="website"
            name="website"
            label={t("Website")}
            placeholder={t("Website")}
            onChange={formik.handleChange}
            value={formik.values.website || ""}
            error={formik.errors.website}
            maxLength={256}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flex: 1,
              margin: "15px 0",
            }}
          >
            <Typography className={classes.detailTitle}>Links</Typography>
            <SocialMediaInput
              formik={formik}
              style={{
                width: "80.5%",
              }}
              containerStyle={{
                borderRadius: "16px",
                height: "50px",
              }}
            />
          </div>
        </>
      </Grid>

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Button
          disabled={isFormUpdated}
          className={classes.submit}
          onClick={() => formik.handleSubmit()}
          style={{
            borderColor: isFormUpdated ? "#bababa" : green,
            // color: isFormUpdated ? "#bababa" : green
          }}
        >
          {t("Save")}
        </Button>
      </div>
    </div>
  );
};

export default EditProfile;
